import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate, useParams } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeft, Building2, Mail, Wallet, Key, Clock,
    CheckCircle, Ban, RotateCcw, X, Plus, Minus,
    Hash, Shield, Package, Percent, DollarSign, Weight,
    FileText,
} from 'lucide-react';

function PartnerDetails() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [partner, setPartner] = React.useState<any>(null);
    const [showWalletModal, setShowWalletModal] = React.useState<string>(''); // 'credit' or 'debit'
    const [walletForm, setWalletForm] = React.useState({ amount: '', description: '', reference: '' });
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    const [showMarkupModal, setShowMarkupModal] = React.useState<boolean>(false);
    const [markupForm, setMarkupForm] = React.useState({ markup_type: 'percentage', markup_value: '' });
    const [markupLoader, setMarkupLoader] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (id) loadPartner();
    }, [id]);

    const loadPartner = () => {
        setLoading(true);
        ApiGatewayApis.getSinglePartner(parseInt(id!))
            .then((response: any) => {
                setLoading(false);
                if (response?.data?.data) setPartner(response.data.data);
            })
            .catch((error: any) => {
                setLoading(false);
                toast.error('Failed to load partner details');
            });
    };

    const handleWallet = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showWalletModal || !id) return;
        setActionLoader(true);

        const data = { amount: parseFloat(walletForm.amount), description: walletForm.description, reference: walletForm.reference };
        const promise = showWalletModal === 'credit'
            ? ApiGatewayApis.creditWallet(parseInt(id), data)
            : ApiGatewayApis.debitWallet(parseInt(id), data);

        promise.then((response: any) => {
            setActionLoader(false);
            if (response?.data?.success) {
                toast.success(`Wallet ${showWalletModal}ed. New balance: ₦${response.data.data?.new_balance?.toLocaleString()}`);
                setShowWalletModal('');
                setWalletForm({ amount: '', description: '', reference: '' });
                loadPartner();
            } else {
                toast.warn(response?.data?.message || 'Failed');
            }
        }).catch((error: any) => {
            setActionLoader(false);
            toast.error(error?.response?.data?.message || 'Error');
        });
    };


    const handleMarkup = (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setMarkupLoader(true);

        ApiGatewayApis.setPartnerMarkup(parseInt(id), {
            markup_type: markupForm.markup_type,
            markup_value: parseFloat(markupForm.markup_value),
        }).then((response: any) => {
            setMarkupLoader(false);
            if (response?.data?.success) {
                toast.success('Markup updated successfully');
                setShowMarkupModal(false);
                loadPartner();
            } else {
                toast.warn(response?.data?.message || 'Failed to update markup');
            }
        }).catch((error: any) => {
            setMarkupLoader(false);
            toast.error(error?.response?.data?.message || 'Failed to update markup');
        });
    };

    // Pre-fill markup form when partner loads
    React.useEffect(() => {
        if (partner) {
            setMarkupForm({
                markup_type: partner.markup_type || 'percentage',
                markup_value: partner.markup_value?.toString() || '0',
            });
        }
    }, [partner]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'suspended': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <>
            <AdminSidebar title="Partner Details" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Partner Details" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>
                    <div className='flex items-center gap-3 mb-5'>
                        <button onClick={() => navigate('/admin/api-gateway/partners')} className='text-gray-500 hover:text-[#E04343]'>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className='text-[20px] font-bold text-[#263238]'>Partner Details</h1>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-20'>
                            <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : !partner ? (
                        <div className='text-center py-20'>
                            <p className='text-[#808080]'>Partner not found</p>
                        </div>
                    ) : (
                        <div className='grid lg:grid-cols-3 gap-5'>

                            {/* Left: Partner Info */}
                            <div className='lg:col-span-2 space-y-5'>
                                {/* Basic Info Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <div className='flex justify-between items-start mb-4'>
                                        <div>
                                            <h2 className='text-[18px] font-bold text-[#263238] flex items-center gap-2'>
                                                <Building2 size={20} className='text-[#E04343]' />
                                                {partner.company_name}
                                            </h2>
                                            <p className='text-[13px] text-[#808080] flex items-center gap-1 mt-1'>
                                                <Mail size={14} /> {partner.contact_email}
                                            </p>
                                        </div>
                                        <span className={`text-[11px] font-semibold px-3 py-1 rounded-full ${getStatusBadge(partner.status)}`}>
                                            {partner.status}
                                        </span>
                                    </div>

                                    <div className='grid md:grid-cols-3 gap-4 mt-4'>
                                        <div className='bg-gray-50 rounded-lg p-3'>
                                            <p className='text-[11px] text-[#808080]'>Rate Limit</p>
                                            <p className='text-[15px] font-semibold'>{partner.rate_limit_per_minute}/min</p>
                                        </div>
                                        <div className='bg-gray-50 rounded-lg p-3'>
                                            <p className='text-[11px] text-[#808080]'>Created</p>
                                            <p className='text-[13px] font-medium'>{partner.created_at ? new Date(partner.created_at).toLocaleDateString() : '-'}</p>
                                        </div>
                                        <div className='bg-gray-50 rounded-lg p-3'>
                                            <p className='text-[11px] text-[#808080]'>NIPOST Prefix</p>
                                            <p className='text-[15px] font-semibold'>{partner.nipost_reference_prefix || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* API Keys */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2 mb-3'>
                                        <Key size={18} /> API Keys
                                    </h3>
                                    {partner.api_keys && partner.api_keys.length > 0 ? (
                                        <div className='space-y-2'>
                                            {partner.api_keys.map((key: any) => (
                                                <div key={key.id} className='flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3'>
                                                    <div>
                                                        <span className='text-[13px] font-mono font-medium'>{key.key_prefix}...</span>
                                                        <span className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                            {key.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <span className='text-[11px] text-[#808080]'>{key.total_requests?.toLocaleString() || 0} requests</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className='text-[13px] text-[#808080]'>No API keys found</p>
                                    )}
                                </div>

                                {/* Recent Transactions */}
                                {partner.recent_transactions && partner.recent_transactions.length > 0 && (
                                    <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                        <h3 className='text-[15px] font-semibold text-[#263238] mb-3'>Recent Wallet Transactions</h3>
                                        <div className='space-y-2'>
                                            {partner.recent_transactions.map((txn: any, idx: number) => (
                                                <div key={idx} className='flex items-center justify-between border-b pb-2 last:border-0'>
                                                    <div>
                                                        <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${txn.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                            {txn.type}
                                                        </span>
                                                        <span className='text-[12px] text-[#808080] ml-2'>{txn.description}</span>
                                                    </div>
                                                    <span className={`text-[13px] font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                                        {txn.type === 'credit' ? '+' : '-'}₦{parseFloat(txn.amount || 0).toLocaleString()}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right: Wallet + Actions */}
                            <div className='space-y-5'>
                                {/* Wallet Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5 border-t-[3px] border-[#E04343]'>
                                    <h3 className='text-[13px] text-[#808080] flex items-center gap-2'>
                                        <Wallet size={16} /> Wallet Balance
                                    </h3>
                                    <h1 className='text-[28px] font-bold text-[#263238] mt-1'>
                                        ₦{parseFloat(partner.wallet_balance || 0).toLocaleString()}
                                    </h1>
                                    <div className='flex gap-2 mt-4'>
                                        <button
                                            onClick={() => setShowWalletModal('credit')}
                                            className='flex-1 flex items-center justify-center gap-1 bg-green-600 text-white text-[13px] py-2 rounded-[5px] hover:bg-green-700'
                                        >
                                            <Plus size={14} /> Credit
                                        </button>
                                        <button
                                            onClick={() => setShowWalletModal('debit')}
                                            className='flex-1 flex items-center justify-center gap-1 bg-red-600 text-white text-[13px] py-2 rounded-[5px] hover:bg-red-700'
                                        >
                                            <Minus size={14} /> Debit
                                        </button>
                                    </div>
                                </div>

                                {/* Pricing Markup Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[13px] text-[#808080] flex items-center gap-2 mb-3'>
                                        <Percent size={16} /> Pricing Markup
                                    </h3>

                                    {partner.markup_value > 0 ? (
                                        <div className='space-y-2'>
                                            <div className='flex justify-between'>
                                                <span className='text-[12px] text-[#808080]'>Type</span>
                                                <span className='text-[13px] font-semibold capitalize'>{partner.markup_type}</span>
                                            </div>
                                            <div className='flex justify-between'>
                                                <span className='text-[12px] text-[#808080]'>Value</span>
                                                <span className='text-[13px] font-semibold'>
                                                    {partner.markup_type === 'percentage'
                                                        ? `${partner.markup_value}%`
                                                        : `₦${parseFloat(partner.markup_value).toLocaleString()}`
                                                    }
                                                    {partner.markup_type === 'per_kg' && '/kg'}
                                                </span>
                                            </div>
                                            <div className='flex justify-between'>
                                                <span className='text-[12px] text-[#808080]'>Example (₦5,000 NIPOST)</span>
                                                <span className='text-[13px] font-semibold text-green-600'>
                                                    {partner.markup_type === 'percentage'
                                                        ? `₦${(5000 * (1 + partner.markup_value / 100)).toLocaleString()}`
                                                        : partner.markup_type === 'flat'
                                                            ? `₦${(5000 + parseFloat(partner.markup_value)).toLocaleString()}`
                                                            : `₦${(5000 + parseFloat(partner.markup_value) * 3).toLocaleString()} (3kg)`
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='bg-orange-50 rounded p-3 mb-2'>
                                            <p className='text-[12px] text-orange-700'>No markup configured — NIPOST costs pass through at 1:1. Skynet earns zero on this partner's shipments.</p>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => setShowMarkupModal(true)}
                                        className='w-full mt-3 flex items-center justify-center gap-1 bg-[#263238] text-white text-[13px] py-2 rounded-[5px] hover:bg-[#1a2228]'
                                    >
                                        <Percent size={14} /> {partner.markup_value > 0 ? 'Edit Markup' : 'Set Markup'}
                                    </button>
                                </div>


                                {/* Shipment Stats */}
                                {partner.shipment_stats && (
                                    <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                        <h3 className='text-[13px] text-[#808080] flex items-center gap-2 mb-3'>
                                            <Package size={16} /> Shipment Stats
                                        </h3>
                                        <div className='space-y-2'>
                                            {Object.entries(partner.shipment_stats).map(([key, value]: any) => (
                                                <div key={key} className='flex justify-between'>
                                                    <span className='text-[12px] text-[#808080] capitalize'>{key.replace(/_/g, ' ')}</span>
                                                    <span className='text-[13px] font-semibold'>{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}


                                {/* Billing */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5 border-t-[3px] border-blue-500'>
                                    <h3 className='text-[13px] text-[#808080] flex items-center gap-2 mb-3'>
                                        <FileText size={16} /> Billing
                                    </h3>
                                    <p className='text-[12px] text-[#808080] mb-3'>
                                        Manage rate agreements and view/generate invoices for this partner.
                                    </p>
                                    <button
                                        onClick={() => navigate(`/admin/api-gateway/partners/${partner.id}/billing`)}
                                        className='w-full flex items-center justify-center gap-2 bg-blue-600 text-white text-[13px] py-2.5 rounded-[5px] hover:bg-blue-700'
                                    >
                                        <FileText size={14} /> Billing & Rate Agreements
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Wallet Modal */}
            {showWalletModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[420px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold capitalize'>{showWalletModal} Wallet</h3>
                            <button onClick={() => setShowWalletModal('')}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleWallet} className='p-5 space-y-4'>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Amount (₦) <span className='text-red-400'>*</span></label>
                                <input type='number' required min={1} step='0.01' value={walletForm.amount}
                                    onChange={(e) => setWalletForm({ ...walletForm, amount: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5' placeholder='50000' />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Description <span className='text-red-400'>*</span></label>
                                <input type='text' required value={walletForm.description}
                                    onChange={(e) => setWalletForm({ ...walletForm, description: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5' placeholder='Reason...' />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Reference</label>
                                <input type='text' value={walletForm.reference}
                                    onChange={(e) => setWalletForm({ ...walletForm, reference: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5' placeholder='TRF-001' />
                            </div>
                            <button type='submit' disabled={actionLoader}
                                className={`${showWalletModal === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white py-2.5 rounded-[5px] w-full text-sm font-medium disabled:opacity-50 flex justify-center gap-2 capitalize`}
                            >
                                {showWalletModal} Wallet
                                <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}


            {showMarkupModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[460px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold'>Configure Pricing Markup</h3>
                            <button onClick={() => setShowMarkupModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleMarkup} className='p-5 space-y-4'>

                            <div className='bg-blue-50 border-l-4 border-blue-500 rounded p-3'>
                                <p className='text-[12px] text-blue-800'>
                                    This markup is added on top of the NIPOST shipping cost. The partner only sees the final total price — never the breakdown.
                                </p>
                            </div>

                            <div>
                                <label className='block mb-2 text-sm font-semibold text-[#19213D]'>Markup Type</label>
                                <div className='grid grid-cols-3 gap-2'>
                                    {[
                                        { value: 'percentage', label: 'Percentage', icon: <Percent size={16} />, desc: 'e.g. 15%' },
                                        { value: 'flat', label: 'Flat Fee', icon: <DollarSign size={16} />, desc: 'e.g. ₦500' },
                                        { value: 'per_kg', label: 'Per KG', icon: <Weight size={16} />, desc: 'e.g. ₦100/kg' },
                                    ].map(opt => (
                                        <button key={opt.value} type='button'
                                            onClick={() => setMarkupForm({ ...markupForm, markup_type: opt.value })}
                                            className={`border-2 rounded-[5px] p-3 text-center transition ${markupForm.markup_type === opt.value
                                                ? 'border-[#E04343] bg-red-50'
                                                : 'border-[#DADADA] hover:border-[#E04343]'
                                                }`}>
                                            <div className='flex justify-center mb-1'>{opt.icon}</div>
                                            <p className='text-[12px] font-[600]'>{opt.label}</p>
                                            <p className='text-[10px] text-[#808080]'>{opt.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>
                                    Markup Value
                                    <span className='text-red-400 ml-1'>*</span>
                                    <span className='text-[11px] text-[#808080] font-normal ml-2'>
                                        {markupForm.markup_type === 'percentage' ? '(%)' : markupForm.markup_type === 'flat' ? '(₦ per shipment)' : '(₦ per kg)'}
                                    </span>
                                </label>
                                <input type='number' required min={0} step='0.01' value={markupForm.markup_value}
                                    onChange={(e) => setMarkupForm({ ...markupForm, markup_value: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder={markupForm.markup_type === 'percentage' ? '15' : markupForm.markup_type === 'flat' ? '500' : '100'} />
                            </div>

                            {/* Live preview */}
                            {markupForm.markup_value && parseFloat(markupForm.markup_value) > 0 && (
                                <div className='bg-gray-50 rounded-[5px] p-3'>
                                    <p className='text-[11px] text-[#808080] mb-2'>Preview (for a ₦5,000 NIPOST shipment, 3kg):</p>
                                    <div className='flex justify-between'>
                                        <span className='text-[13px]'>NIPOST cost</span>
                                        <span className='text-[13px]'>₦5,000</span>
                                    </div>
                                    <div className='flex justify-between text-green-600'>
                                        <span className='text-[13px]'>Skynet markup</span>
                                        <span className='text-[13px] font-semibold'>
                                            +₦{markupForm.markup_type === 'percentage'
                                                ? (5000 * parseFloat(markupForm.markup_value) / 100).toLocaleString()
                                                : markupForm.markup_type === 'flat'
                                                    ? parseFloat(markupForm.markup_value).toLocaleString()
                                                    : (parseFloat(markupForm.markup_value) * 3).toLocaleString()
                                            }
                                        </span>
                                    </div>
                                    <div className='flex justify-between border-t pt-1 mt-1 font-bold'>
                                        <span className='text-[13px]'>Partner pays</span>
                                        <span className='text-[13px]'>
                                            ₦{markupForm.markup_type === 'percentage'
                                                ? (5000 * (1 + parseFloat(markupForm.markup_value) / 100)).toLocaleString()
                                                : markupForm.markup_type === 'flat'
                                                    ? (5000 + parseFloat(markupForm.markup_value)).toLocaleString()
                                                    : (5000 + parseFloat(markupForm.markup_value) * 3).toLocaleString()
                                            }
                                        </span>
                                    </div>
                                </div>
                            )}

                            <button type='submit' disabled={markupLoader}
                                className='bg-[#E04343] text-white py-2.5 rounded-[5px] w-full text-sm font-medium disabled:opacity-50 hover:bg-[#c73838] flex justify-center gap-2'>
                                Save Markup
                                <Oval visible={markupLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default PartnerDetails;